import React, { useEffect, useRef } from "react";
import { useAppSelector, useAppDispatch } from "../../../../common/hooks/hooks";
import { setActiveTab, UserTab } from "../../userSlice";
import { useGetUserProfileQuery } from "../../api/userAuthApi";
import { useUserLogout } from "../../hooks/useUserLogout";
import { openGlobalModal } from "../../../../common/features/globalModal/globalModalSlice";
import ProfileSidebar from "./ProfileSidebar";
import ProfileContent from "./ProfileContent/ProfileContent";

const ProfileDashboard: React.FC = () => {
  const dispatch = useAppDispatch();
  const activeTab = useAppSelector(
    (state) => state.user?.activeTab || "dashboard",
  );

  const { data: profile, isLoading: isProfileLoading } =
    useGetUserProfileQuery();
  const { handleLogout, isLoading: isLoggingOut } = useUserLogout();

  // Ref to prevent double-firing the modal in React Strict Mode
  const hasCheckedForWelcome = useRef(false);

  // --- WELCOME COUPON LOGIC ---
  useEffect(() => {
    if (!profile || isProfileLoading || hasCheckedForWelcome.current) return;

    // Check if the user just registered via the flag set in RegisterForm
    const isNewUser = sessionStorage.getItem("theme_just_registered");

    if (isNewUser && profile.coupons && profile.coupons.length > 0) {
      // Show the modal
      dispatch(
        openGlobalModal({
          contentKey: "welcomeCoupons",
          title: "", // The component handles its own header
          contentProps: {
            coupons: profile.coupons,
          },
        }),
      );

      // Clear the flag so it doesn't show again on reload
      sessionStorage.removeItem("theme_just_registered");
    }

    hasCheckedForWelcome.current = true;
  }, [profile, isProfileLoading, dispatch]);

  const handleTabChange = (tab: UserTab) => {
    dispatch(setActiveTab(tab));
  };

  return (
    <div className="profile-dashboard">
      <div className="profile-dashboard__container">
        <ProfileSidebar
          activeTab={activeTab}
          onTabChange={handleTabChange}
          onLogout={handleLogout}
        />
        <main className="profile-content">
          {/* Show loader while logging out to lock UI interaction */}
          {isLoggingOut ? (
            <div
              className="profile-content__loading"
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "200px",
              }}
            >
              <div className="loader-spinner"></div>
            </div>
          ) : (
            <ProfileContent
              activeTab={activeTab}
              profile={profile}
              isLoading={isProfileLoading}
              onTabChange={handleTabChange}
            />
          )}
        </main>
      </div>
    </div>
  );
};

export default ProfileDashboard;
